import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { THEME } from '../../utils/theme';
import api from '../../utils/api';
import Avatar from '../ui/Avatar';

const NotificationsModal = ({ isOpen, onClose, activeTab, setActiveTab, activities, activityCount, dmMessages, dmUnreadCount, onMarkActivitiesSeen, onActivityClick, onMatchStatus, onSendDM, onMarkDMsRead, currentUser, squadMembers = [] }) => {
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [reactions, setReactions] = useState({});
  const [reactingTo, setReactingTo] = useState(null);
  const [matchedActivities, setMatchedActivities] = useState(new Set()); // Track which activities user clicked "match" on
  const [expandedBatches, setExpandedBatches] = useState(new Set()); // For batch expansion
  const messagesEndRef = useRef(null);

  // Handle match status with feedback
  const handleMatchStatusWithFeedback = async (activity) => {
    setMatchedActivities(prev => new Set([...prev, activity.id]));
    await onMatchStatus(activity);
    // Visual feedback persists - the button changes to "Matched!"
  };

  // Group activities by user within 5-minute windows for batching
  const groupActivities = (activityList) => {
    const groups = [];
    let currentGroup = null;
    const BATCH_WINDOW_MS = 5 * 60 * 1000; // 5 minutes

    for (const activity of activityList) {
      const activityTime = new Date(activity.created_at).getTime();

      // Start new group if different user or time gap > 5 min
      if (!currentGroup ||
          currentGroup.user.username !== activity.user.username ||
          currentGroup.latestTime - activityTime > BATCH_WINDOW_MS) {
        if (currentGroup) groups.push(currentGroup);
        currentGroup = {
          id: `batch-${activity.id}`,
          user: activity.user,
          activities: [activity],
          latestTime: activityTime,
          earliestTime: activityTime
        };
      } else {
        currentGroup.activities.push(activity);
        currentGroup.earliestTime = activityTime;
      }
    }
    if (currentGroup) groups.push(currentGroup);
    return groups;
  };

  const activityGroups = groupActivities(activities);
  const toggleBatchExpand = (batchId) => {
    setExpandedBatches(prev => {
      const next = new Set(prev);
      if (next.has(batchId)) next.delete(batchId);
      else next.add(batchId);
      return next;
    });
  };

  // Load reactions for all activities
  useEffect(() => {
    if (isOpen && activities.length > 0) {
      activities.forEach(async (activity) => {
        try {
          const data = await api.get(`/api/activities/${activity.id}/reactions`);
          setReactions(prev => ({ ...prev, [activity.id]: data }));
        } catch (err) {}
      });
    }
  }, [isOpen, activities]);

  useEffect(() => {
    if (isOpen && activeTab === 'dm') {
      onMarkDMsRead();
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  }, [isOpen, activeTab, dmMessages, onMarkDMsRead]);

  const handleSend = async () => {
    if (!newMessage.trim() || sending) return;
    setSending(true);
    const result = await onSendDM(newMessage.trim());
    if (result) setNewMessage('');
    setSending(false);
  };

  const handleReact = async (activityId, emoji) => {
    setReactingTo(activityId);
    try {
      await api.post(`/api/activities/${activityId}/react?emoji=${encodeURIComponent(emoji)}`);
      // Refresh reactions for this activity
      const data = await api.get(`/api/activities/${activityId}/reactions`);
      setReactions(prev => ({ ...prev, [activityId]: data }));
    } catch (err) { console.error(err); }
    finally { setReactingTo(null); }
  };

  if (!isOpen) return null;

  const formatTimeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  const getActivityText = (activity) => {
    const name = activity.user.display_name;
    const game = activity.game_name || 'a game';
    switch (activity.activity_type) {
      case 'rating': return activity.details?.rating ? `${name} rated ${game} ${activity.details.rating}/10` : `${name} updated rating for ${game}`;
      case 'message': return `${name} commented on ${game}`;
      case 'mention': return `${name} mentioned you in ${game}`;
      case 'recommend': return `${name} recommended ${game}`;
      case 'status_change':
        const statusLabels = { played: 'played', wtp: 'wants to play', wtf: 'marked unfamiliar', never: 'marked as never again', unplayed: 'unmarked', shelved: 'shelved' };
        return `${name} ${statusLabels[activity.details?.status] || 'updated status for'} ${game}`;
      case 'status_match':
        const matchStatusLabels = { played: 'Played', wtp: 'WTP', wtf: 'WTF', never: 'Never', unplayed: 'Unplayed', shelved: 'Shelved' };
        return `🎉 You both marked ${game} as ${matchStatusLabels[activity.details?.status] || activity.details?.status}!`;
      case 'remote_play_hosting': return `${name} is hosting ${game} via Remote Play`;
      case 'swipe_session_started':
        const gameCount = activity.details?.game_count || 'some';
        return `${activity.details?.started_by || name} started a Squad Swipe with ${gameCount} games`;
      default: return `${name} updated ${game}`;
    }
  };

  const getActivityIcon = (type) => ({ rating: '⭐', message: '💬', mention: '📣', recommend: '👍', status_change: '📋', status_match: '🤝', remote_play_hosting: '🎮', swipe_session_started: '👆' }[type] || '📌');
  const getStatusLabel = (status) => ({ played: 'Played', wtp: 'WTP', wtf: 'WTF', never: 'Never', unplayed: 'Unplayed', shelved: 'Shelved' }[status] || status);
  // Get "other user" dynamically - for 2 users this is the other one, for N users this is a placeholder
  const otherUser = activities.length > 0 ? activities[0]?.user?.display_name : 'Other';

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(5,5,10,0.88)', backdropFilter: 'blur(32px) saturate(180%)', WebkitBackdropFilter: 'blur(32px) saturate(180%)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95, y: 10 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ duration: 0.2 }} style={{ background: `linear-gradient(165deg, ${THEME.bgCard} 0%, ${THEME.bgElevated} 100%)`, borderRadius: '24px', width: '90%', maxWidth: '500px', maxHeight: '80vh', display: 'flex', flexDirection: 'column', border: `1px solid ${THEME.glassBorder}`, boxShadow: THEME.modalShadow }} onClick={e => e.stopPropagation()}>
        {/* Header with tabs */}
        <div style={{ padding: '12px 16px', borderBottom: `1px solid ${THEME.glassBorder}`, background: `linear-gradient(180deg, ${THEME.bgElevated}80 0%, transparent 100%)` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <span style={{ fontWeight: '700', color: THEME.text, fontSize: '16px' }}>📬 Notifications</span>
            <motion.button whileHover={{ scale: 1.1, backgroundColor: THEME.bgActive }} whileTap={{ scale: 0.95 }} onClick={onClose} style={{ background: THEME.bgHover, border: `1px solid ${THEME.glassBorder}`, color: THEME.textMuted, fontSize: '20px', cursor: 'pointer', borderRadius: '10px', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</motion.button>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={() => setActiveTab('activity')} style={{ flex: 1, padding: '10px', background: activeTab === 'activity' ? THEME.omega : THEME.bg, color: activeTab === 'activity' ? '#000' : THEME.textMuted, border: `1px solid ${activeTab === 'activity' ? THEME.omega : THEME.border}`, borderRadius: '8px', fontWeight: '600', fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
              Activity {activityCount > 0 && <span style={{ background: activeTab === 'activity' ? 'rgba(0,0,0,0.3)' : THEME.omega, color: activeTab === 'activity' ? '#000' : '#fff', padding: '2px 6px', borderRadius: '10px', fontSize: '11px' }}>{activityCount}</span>}
            </button>
            <button onClick={() => setActiveTab('dm')} style={{ flex: 1, padding: '10px', background: activeTab === 'dm' ? THEME.supe : THEME.bg, color: activeTab === 'dm' ? '#000' : THEME.textMuted, border: `1px solid ${activeTab === 'dm' ? THEME.supe : THEME.border}`, borderRadius: '8px', fontWeight: '600', fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
              DMs {dmUnreadCount > 0 && <span style={{ background: activeTab === 'dm' ? 'rgba(0,0,0,0.3)' : THEME.supe, color: activeTab === 'dm' ? '#000' : '#fff', padding: '2px 6px', borderRadius: '10px', fontSize: '11px' }}>{dmUnreadCount}</span>}
            </button>
          </div>
        </div>

        {/* Activity Tab */}
        {activeTab === 'activity' && (
          <>
            <div style={{ padding: '8px 16px', borderBottom: `1px solid ${THEME.border}`, display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={onMarkActivitiesSeen} style={{ background: 'transparent', border: 'none', color: THEME.textMuted, fontSize: '12px', cursor: 'pointer' }}>Mark all read</button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', minHeight: '300px' }}>
              {activities.length === 0 ? (
                <div style={{ padding: '60px 20px', textAlign: 'center', color: THEME.textMuted }}>No recent activity</div>
              ) : activityGroups.map(group => {
                const isBatched = group.activities.length > 2;
                const isExpanded = expandedBatches.has(group.id);
                const unreadCount = group.activities.filter(a => !a.read).length;

                // Render batched header for 3+ activities from same user
                if (isBatched && !isExpanded) {
                  return (
                    <div key={group.id} style={{ padding: '12px 16px', borderBottom: `1px solid ${THEME.border}`, background: unreadCount > 0 ? `${THEME.omega}08` : 'transparent', cursor: 'pointer' }} onClick={() => toggleBatchExpand(group.id)}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '14px' }}>📦</span>
                        <span style={{ color: THEME.text, fontSize: '13px', fontWeight: '600' }}>
                          {group.user.display_name} updated {group.activities.length} games
                        </span>
                        {unreadCount > 0 && <span style={{ background: THEME.omega, color: '#000', padding: '2px 6px', borderRadius: '10px', fontSize: '10px', fontWeight: '600' }}>{unreadCount} new</span>}
                        <span style={{ color: THEME.textMuted, fontSize: '11px', marginLeft: 'auto' }}>tap to expand ▼</span>
                      </div>
                      <div style={{ display: 'flex', gap: '4px', marginTop: '8px', flexWrap: 'wrap' }}>
                        {group.activities.slice(0, 4).map(a => a.game_header_image && (
                          <img key={a.id} src={a.game_header_image} alt="" style={{ width: '40px', height: '22px', objectFit: 'cover', borderRadius: '3px', opacity: 0.8 }} />
                        ))}
                        {group.activities.length > 4 && <span style={{ fontSize: '11px', color: THEME.textMuted, alignSelf: 'center' }}>+{group.activities.length - 4} more</span>}
                      </div>
                    </div>
                  );
                }

                // Render individual activities (or expanded batch)
                const activitiesToRender = isBatched ? group.activities : group.activities;
                return (
                  <div key={group.id}>
                    {isBatched && isExpanded && (
                      <div style={{ padding: '8px 16px', background: THEME.bgElevated, borderBottom: `1px solid ${THEME.border}`, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }} onClick={() => toggleBatchExpand(group.id)}>
                        <span style={{ fontSize: '12px', color: THEME.textMuted }}>{group.user.display_name}'s updates</span>
                        <span style={{ fontSize: '11px', color: THEME.textMuted }}>▲ collapse</span>
                      </div>
                    )}
                    {activitiesToRender.map(activity => (
                <div key={activity.id} style={{ padding: '12px 16px', borderBottom: `1px solid ${THEME.border}`, display: 'flex', gap: '12px', alignItems: 'flex-start', background: activity.read ? 'transparent' : `${THEME.omega}08`, cursor: 'pointer' }} onClick={() => onActivityClick(activity)}>
                  {activity.game_header_image && <img src={activity.game_header_image} alt="" style={{ width: '50px', height: '28px', objectFit: 'cover', borderRadius: '4px', flexShrink: 0 }} />}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                      <span style={{ fontSize: '14px' }}>{getActivityIcon(activity.activity_type)}</span>
                      <span style={{ color: THEME.text, fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{getActivityText(activity)}</span>
                      {!activity.read && <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: THEME.omega, flexShrink: 0 }} />}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '4px' }}>
                      <span style={{ color: THEME.textDim, fontSize: '11px' }}>{formatTimeAgo(activity.created_at)}</span>
                      {/* Reaction buttons */}
                      <div style={{ display: 'flex', gap: '4px' }} onClick={e => e.stopPropagation()}>
                        {['👍', '🎮', '🔥'].map(emoji => {
                          const actReactions = reactions[activity.id] || {};
                          const hasReacted = actReactions[emoji]?.users?.includes(currentUser?.username);
                          const count = actReactions[emoji]?.count || 0;
                          return (
                            <button
                              key={emoji}
                              onClick={() => handleReact(activity.id, emoji)}
                              disabled={reactingTo === activity.id}
                              style={{
                                background: hasReacted ? `${THEME.omega}30` : THEME.bg,
                                border: `1px solid ${hasReacted ? THEME.omega : THEME.border}`,
                                borderRadius: '12px',
                                padding: '2px 6px',
                                fontSize: '12px',
                                cursor: reactingTo === activity.id ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '3px',
                                opacity: reactingTo === activity.id ? 0.5 : 1
                              }}
                            >
                              {emoji}{count > 0 && <span style={{ fontSize: '10px', color: THEME.textMuted }}>{count}</span>}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    {/* Show user's current status and conditional match button */}
                    {activity.activity_type === 'status_change' && activity.details?.status && (
                      <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                        {/* Show user's current status for this game */}
                        {activity.my_status && (
                          <span style={{ fontSize: '11px', color: THEME.textMuted, background: THEME.bg, padding: '3px 8px', borderRadius: '4px', border: `1px solid ${THEME.border}` }}>
                            You: {getStatusLabel(activity.my_status)}
                          </span>
                        )}
                        {/* Show match button only if statuses differ (or no status yet) */}
                        {activity.my_status !== activity.details.status ? (
                          matchedActivities.has(activity.id) ? (
                            <span style={{ fontSize: '11px', color: THEME.success, fontWeight: '600' }}>✓ Matched!</span>
                          ) : (
                            <button onClick={(e) => { e.stopPropagation(); handleMatchStatusWithFeedback(activity); }} style={{ background: `linear-gradient(135deg, ${THEME.omega}, ${THEME.supe})`, color: '#000', border: 'none', borderRadius: '6px', padding: '5px 10px', fontSize: '11px', fontWeight: '600', cursor: 'pointer' }}>
                              Mark as {getStatusLabel(activity.details.status)} too
                            </button>
                          )
                        ) : (
                          <span style={{ fontSize: '11px', color: THEME.success, fontWeight: '600' }}>✓ Already {getStatusLabel(activity.my_status)}</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* DM Tab */}
        {activeTab === 'dm' && (
          <>
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px', minHeight: '300px' }}>
              {dmMessages.length === 0 ? (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: THEME.textMuted }}>No messages yet. Say hi to {otherUser}!</div>
              ) : dmMessages.map(m => {
                // Parse mentions in message - dynamic based on squad members
                const renderMessageWithMentions = (text) => {
                  const memberUsernames = squadMembers.map(m => m.username);
                  const mentionPattern = new RegExp(`(@${memberUsernames.join('|@')})`, 'gi');
                  const parts = text.split(mentionPattern);
                  return parts.map((part, i) => {
                    const lowerPart = part.toLowerCase();
                    const matchedMember = squadMembers.find(m => lowerPart === `@${m.username.toLowerCase()}`);
                    if (matchedMember) {
                      const color = matchedMember.theme_color || THEME.success;
                      return <span key={i} style={{ color, fontWeight: '600', background: `${color}20`, borderRadius: '4px', padding: '0 4px' }}>@{matchedMember.username}</span>;
                    }
                    return part;
                  });
                };
                return (
                  <div key={m.id} style={{ display: 'flex', flexDirection: 'column', alignItems: m.is_mine ? 'flex-end' : 'flex-start' }}>
                    <div style={{ maxWidth: '80%', padding: '10px 14px', borderRadius: m.is_mine ? '14px 14px 4px 14px' : '14px 14px 14px 4px', background: m.is_mine ? `linear-gradient(135deg, ${m.sender.color}40, ${m.sender.color}20)` : THEME.bgElevated, border: `1px solid ${m.is_mine ? m.sender.color + '40' : THEME.border}` }}>
                      <div style={{ color: THEME.text, fontSize: '14px', lineHeight: '1.4' }}>{renderMessageWithMentions(m.message)}</div>
                    </div>
                    <span style={{ color: THEME.textDim, fontSize: '10px', marginTop: '4px', padding: '0 4px' }}>{new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
            <div style={{ padding: '12px 16px', borderTop: `1px solid ${THEME.border}` }}>
              <div style={{ display: 'flex', gap: '6px', marginBottom: '8px', flexWrap: 'wrap' }}>
                {squadMembers.filter(m => m.username !== currentUser?.username).map(member => {
                  const color = member.theme_color || THEME.success;
                  return (
                    <button key={member.username} onClick={() => setNewMessage(prev => prev + `@${member.username} `)} style={{ background: `${color}20`, color: color, border: `1px solid ${color}40`, borderRadius: '12px', padding: '4px 10px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>@{member.username}</button>
                  );
                })}
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <input type="text" value={newMessage} onChange={e => setNewMessage(e.target.value)} onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()} placeholder={`Message ${otherUser}... (use @ to mention)`} style={{ flex: 1, background: THEME.bg, border: `1px solid ${THEME.border}`, borderRadius: '10px', padding: '12px 16px', color: THEME.text, fontSize: '14px', outline: 'none' }} />
                <button onClick={handleSend} disabled={sending || !newMessage.trim()} style={{ background: `linear-gradient(135deg, ${THEME.omega}, ${THEME.supe})`, color: '#000', border: 'none', borderRadius: '10px', padding: '12px 20px', fontWeight: '600', cursor: (sending || !newMessage.trim()) ? 'not-allowed' : 'pointer', opacity: (sending || !newMessage.trim()) ? 0.5 : 1 }}>{sending ? '...' : 'Send'}</button>
              </div>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default NotificationsModal;
